import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { generateDateFunctionSql } from '../utils/sqlGenerator';

interface DateTimeFunctionBuilderProps {
  columnName: string;
  onSqlGenerate: (sql: string) => void;
}

interface DateFunction {
  value: string;
  label: string;
  params: Array<{
    name: string;
    label: string;
    type: 'select' | 'text';
    options?: Array<{ value: string; label: string }>;
    default?: string;
  }>;
  example: string;
}

const DATE_FUNCTIONS: DateFunction[] = [
  {
    value: 'DATE_TRUNC',
    label: 'DATE_TRUNC - Truncate to precision',
    params: [
      {
        name: 'unit',
        label: 'Truncate to',
        type: 'select',
        options: [
          { value: 'year', label: 'Year' },
          { value: 'quarter', label: 'Quarter' },
          { value: 'month', label: 'Month' },
          { value: 'week', label: 'Week' },
          { value: 'day', label: 'Day' },
          { value: 'hour', label: 'Hour' },
          { value: 'minute', label: 'Minute' },
          { value: 'second', label: 'Second' },
        ],
        default: 'month'
      }
    ],
    example: "DATE_TRUNC('month', '2024-01-15') → '2024-01-01'"
  },
  {
    value: 'EXTRACT',
    label: 'EXTRACT - Extract component',
    params: [
      {
        name: 'part',
        label: 'Extract',
        type: 'select',
        options: [
          { value: 'year', label: 'Year' },
          { value: 'quarter', label: 'Quarter' },
          { value: 'month', label: 'Month' },
          { value: 'week', label: 'Week' },
          { value: 'day', label: 'Day' },
          { value: 'hour', label: 'Hour' },
          { value: 'minute', label: 'Minute' },
          { value: 'second', label: 'Second' },
          { value: 'dow', label: 'Day of Week (0-6)' },
          { value: 'doy', label: 'Day of Year (1-366)' },
        ],
        default: 'year'
      }
    ],
    example: "EXTRACT(year FROM '2024-01-15') → 2024"
  },
  {
    value: 'DATE_DIFF',
    label: 'DATE_DIFF - Calculate difference',
    params: [
      {
        name: 'unit',
        label: 'Unit',
        type: 'select',
        options: [
          { value: 'year', label: 'Years' },
          { value: 'quarter', label: 'Quarters' },
          { value: 'month', label: 'Months' },
          { value: 'week', label: 'Weeks' },
          { value: 'day', label: 'Days' },
          { value: 'hour', label: 'Hours' },
          { value: 'minute', label: 'Minutes' },
          { value: 'second', label: 'Seconds' },
        ],
        default: 'day'
      },
      {
        name: 'end_date',
        label: 'End Date (column name or CURRENT_DATE)',
        type: 'text',
        default: 'CURRENT_DATE'
      }
    ],
    example: "DATE_DIFF('day', start_date, end_date) → 14"
  },
  {
    value: 'STRFTIME',
    label: 'STRFTIME - Format as string',
    params: [
      {
        name: 'format',
        label: 'Format String',
        type: 'text',
        default: '%Y-%m-%d'
      }
    ],
    example: "STRFTIME('2024-01-15', '%Y-%m') → '2024-01'"
  },
  {
    value: 'CURRENT_DATE',
    label: 'CURRENT_DATE - Current date',
    params: [],
    example: "CURRENT_DATE → '2024-12-09'"
  },
  {
    value: 'CURRENT_TIMESTAMP',
    label: 'CURRENT_TIMESTAMP - Current timestamp',
    params: [],
    example: "CURRENT_TIMESTAMP → '2024-12-09 10:30:00'"
  },
];

export const DateTimeFunctionBuilder: React.FC<DateTimeFunctionBuilderProps> = ({
  columnName,
  onSqlGenerate,
}) => {
  const [selectedFunction, setSelectedFunction] = useState('DATE_TRUNC');
  const [parameters, setParameters] = useState<Record<string, string>>({
    unit: 'month',
  });

  const currentFunction = DATE_FUNCTIONS.find(f => f.value === selectedFunction);

  useEffect(() => {
    const sql = generateDateFunctionSql(columnName, selectedFunction, parameters);
    onSqlGenerate(sql);
  }, [selectedFunction, parameters, columnName, onSqlGenerate]);

  const handleParameterChange = (paramName: string, value: string) => {
    setParameters(prev => ({ ...prev, [paramName]: value }));
  };

  // Reset parameters to the new function's defaults whenever selectedFunction
  // changes — converted from a useEffect to a guarded render-time adjustment
  // (react-hooks/set-state-in-effect). `hasInitializedParams` mirrors the
  // effect always firing once after mount (regardless of the initial value),
  // and `prevSelectedFunctionForParams` mirrors the `[selectedFunction]` dep;
  // both are set unconditionally inside the guard so it converges in one
  // extra render. Preserves the original's on-mount reset (which replaced
  // `parameters` with a fresh, content-equal object and so caused the
  // `onSqlGenerate` effect above to re-fire once more with the same SQL).
  const [hasInitializedParams, setHasInitializedParams] = useState(false);
  const [prevSelectedFunctionForParams, setPrevSelectedFunctionForParams] = useState(selectedFunction);
  if (!hasInitializedParams || selectedFunction !== prevSelectedFunctionForParams) {
    setHasInitializedParams(true);
    setPrevSelectedFunctionForParams(selectedFunction);
    if (currentFunction) {
      const newParams: Record<string, string> = {};
      currentFunction.params.forEach(param => {
        newParams[param.name] = param.default || '';
      });
      setParameters(newParams);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <Label>Date/Time Function</Label>
        <Select value={selectedFunction} onValueChange={setSelectedFunction}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DATE_FUNCTIONS.map((func) => (
              <SelectItem key={func.value} value={func.value}>
                {func.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {currentFunction && currentFunction.params.length > 0 && (
        <div className="space-y-3">
          {currentFunction.params.map((param) => (
            <div key={param.name}>
              <Label>{param.label}</Label>
              {param.type === 'select' && param.options ? (
                <Select
                  value={parameters[param.name] || param.default}
                  onValueChange={(value) => handleParameterChange(param.name, value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {param.options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type="text"
                  value={parameters[param.name] || param.default}
                  onChange={(e) => handleParameterChange(param.name, e.target.value)}
                  placeholder={param.default}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {currentFunction && (
        <div className="text-sm text-muted-foreground">
          <strong>Example:</strong>
          <div className="mt-1">{currentFunction.example}</div>
          {selectedFunction === 'STRFTIME' && (
            <div className="mt-2">
              <strong>Common formats:</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>%Y-%m-%d: 2024-01-15</li>
                <li>%Y-%m: 2024-01</li>
                <li>%d/%m/%Y: 15/01/2024</li>
                <li>%B %d, %Y: January 15, 2024</li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
